/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import type { NetworkError } from '@apollo/client/errors';
import { type ErrorHandler } from '@apollo/client/link/error';
import { notifications } from '@mantine/notifications';
import Session from 'supertokens-web-js/recipe/session';

import { envIs } from '@fai/friendli-suite/shared/environments/conditionals';
import {
  reportFromApolloErrorLinkToSentry,
  reportNetworkErrorToSentry,
} from '@fai/third-party/sentry';
import { errorProps } from '@fai/ui/components/notification/props';

/**
 * Log any GraphQL errors or network error that occurred
 */
const errorHandler: ErrorHandler = ({ networkError, operation, graphQLErrors }) => {
  Session.doesSessionExist().then((sessionExists) => {
    if (!sessionExists) return;

    if (graphQLErrors) {
      reportFromApolloErrorLinkToSentry({ operation, errors: graphQLErrors });

      notifications.show({
        message: envIs.PRODUCTION
          ? 'Something went wrong...'
          : `[GraphQL Operation]: ${operation.operationName} failed`,
        ...errorProps,
      });
    }

    if (networkError) {
      notifications.show({
        message: 'Check your connection and try again',
        ...errorProps,
      });

      /**
       * @adhoc NetworkError from `@apollo/client/errors` does not have statusCode in type definition
       * but actually can have statusCode for certain cases
       * @url https://github.com/apollographql/apollo-link/issues/300
       */
      const _networkError = networkError as NetworkError & {
        statusCode: number | undefined;
      };

      /**
       * [PC-4103] Sentry should not capture errors with status code 4xx
       * It filters not only session-related errors but all 4xx errors.
       * For example, it does not send user faults such as 404 Not Found or 429 Too Many Requests to Sentry.
       */
      if (_networkError.statusCode && _networkError.statusCode.toString().startsWith('4')) {
        return;
      }

      reportNetworkErrorToSentry(networkError);
    }
  });
};

export default errorHandler;
